import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Stack,
    Paper,
    Avatar,
    Badge,
    Divider,
    Pagination,
    Box,
    Skeleton,
} from '@mui/material';
import { deepPurple } from '@mui/material/colors';
import { format } from 'date-fns';
import { CSSTransition } from 'react-transition-group';
import api from '../api';
import './styles.css';
import Layout from '../Layout';
const limit = 20;
/**
 * A single notification card
 */
function NotificationCard({ n, onClick }) {
    const isUnread = !n.readAt;
    const initials = n.actor?.name?.[0] ?? 'U';

    // remove any "Open tracker: <url>" and everything after it
    const cleanBody = n.body.replace(/Open tracker:[\s\S]*$/i, '').trim();

    return (
        <CSSTransition in={true} appear timeout={300} classNames="slide">
            <Paper
                elevation={isUnread ? 4 : 1}
                sx={{
                    p: 2,
                    cursor: 'pointer',
                    bgcolor: isUnread ? 'background.paper' : 'grey.50',
                    borderLeft: isUnread ? '4px solid' : 'none',
                    borderColor: isUnread ? 'primary.main' : 'transparent',
                    '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.15s ease-in-out',
                    },
                }}
                onClick={onClick}
            >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Badge
                        color="primary"
                        variant="dot"
                        invisible={!isUnread}
                        overlap="circular"
                    >
                        <Avatar sx={{ bgcolor: deepPurple[500] }}>{initials}</Avatar>
                    </Badge>
                    <Box flex={1}>
                        <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            gutterBottom
                        >
                            {n.title}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'pre-line' }}
                        >
                            {cleanBody}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.disabled"
                            display="block"
                            mt={1}
                        >
                            {format(new Date(n.createdAt), 'dd MMM yyyy • HH:mm')}
                        </Typography>
                    </Box>
                </Stack>
            </Paper>
        </CSSTransition>
    );
}

const skeletons = Array.from({ length: limit }).map((_, idx) => (
    <Paper
        key={idx}
        elevation={1}
        sx={{
            p: 2,
            bgcolor: 'grey.100',
        }}
    >
        <Stack direction="row" spacing={2} alignItems="flex-start">
            <Skeleton variant="circular" width={40} height={40} />
            <Box flex={1}>
                <Skeleton width="60%" height={24} style={{ marginBottom: 8 }} />
                <Skeleton width="80%" />
                <Skeleton width="40%" style={{ marginTop: 8 }} />
            </Box>
        </Stack>
    </Paper>
));
export default function NotificationsPage() {
    const [notifications, setNotifications] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const limit = 20;

    // load one page
    const loadPage = async (pageNum) => {
        setLoading(true);
        try {
            const res = await api.get('notifications', {
                params: { page: pageNum, limit },
            });
            const { items, totalCount } = res.data;
            setNotifications(items);
            setTotalPages(Math.ceil(totalCount / limit));
        } catch (err) {
            console.error('Failed to fetch notifications', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPage(page);

    }, [page]);

    const handleClick = async (n) => {
        try {
            if (!n.readAt) {
                await api.patch(`notifications/${n._id}/read`);
                setNotifications(prev =>
                    prev.map(x =>
                        x._id === n._id ? { ...x, readAt: new Date().toISOString() } : x
                    )
                );
            }
        } catch (err) {
            console.warn('Could not mark read:', err);
        } finally {
            window.location.href = `/Edit/${n.tracker.uniqueIdentifier}`;
        }
    };


    return (
        <Layout title="Notifications">
            <Container maxWidth="md" sx={{ mt: 6, mb: 4 }}>
                <Typography variant="h4" gutterBottom fontWeight={700} textAlign="center">
                    Notifications
                </Typography>

                <Stack spacing={2} divider={<Divider flexItem />}>
                    {loading
                        ? skeletons
                        : notifications.map((n) => (
                            <NotificationCard key={n._id} n={n} onClick={() => handleClick(n)} />
                        ))}

                    {!loading && notifications.length === 0 && (
                        <Typography align="center" color="text.secondary" sx={{ py: 6 }}>
                            You're all caught up!
                        </Typography>
                    )}
                </Stack>

                <Box display="flex" justifyContent="center" mt={4}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, p) => setPage(p)}
                        color="primary"
                        variant="outlined"
                        shape="rounded"
                    />
                </Box>
            </Container>
        </Layout>
    );
}
